import AdminOptionsMenu from "@/components/AdminOptionsMenu/AdminOptionsMenu";
import { useLoginModal } from "@/components/LoginModal/useLoginModal";
import { UserApi } from "@/api/user.api";
import { $account } from "@/stores/account";
import { $activeAdminCartId } from "@/stores/adminWorkspace";
import { $cart, CartService } from "@/stores/cart";
import { cn } from "@/utils/cn";
import { markStaffBrowser } from "@/utils/staffBrowser";
import { Button } from "@mantine/core";
import { useStore } from "@nanostores/react";
import { ChartBar, List, ShoppingCart, User, X } from "@phosphor-icons/react";
import {
  Fragment,
  ReactNode,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";

const MENU_TRANSITION_MS = 300;

function NavButton({
  label,
  onClick,
  icon,
  variant,
  to,
  padding,
}: {
  label: string;
  onClick?: () => void;
  icon?: ReactNode;
  variant?: "button";
  to?: string;
  padding?: number;
}) {
  if (to) {
    return (
      <Button
        style={{
          fontSize: 16,
          padding: variant === "button" ? "0 24px" : 0,
          fontWeight: 600,
          background: variant === "button" ? "#90D9D9" : "none",
          color: "black",
          height: 42,
        }}
        styles={{
          inner: {
            justifyContent: `${variant === "button" ? "center" : "flex-start"}`,
          },
        }}
        radius={variant === "button" ? 100 : 0}
        onClick={onClick}
        leftSection={
          icon ? (
            <div className="relative" style={{ padding: padding ?? 0 }}>
              {icon}
            </div>
          ) : undefined
        }
        component={Link}
        to={to}
      >
        {label}
      </Button>
    );
  }

  return (
    <Button
      style={{
        fontSize: 16,
        padding: variant === "button" ? "0 24px" : 0,
        fontWeight: 600,
        background: variant === "button" ? "#90D9D9" : "none",
        color: "black",
        height: 42,
      }}
      styles={{
        inner: {
          justifyContent: `${variant === "button" ? "center" : "flex-start"}`,
        },
      }}
      radius={variant === "button" ? 100 : 0}
      onClick={onClick}
      leftSection={
        icon ? (
          <div className="relative" style={{ padding: padding ?? 0 }}>
            {icon}
          </div>
        ) : undefined
      }
    >
      {label}
    </Button>
  );
}

// Exported (2026-07-27) so NavbarDashboard collapses with the SAME menu
// rather than a second copy — the frameMatRetry lesson: one helper, every
// caller inherits it.
export function MobileMenu({
  children,
  isOpen,
  onToggle,
}: {
  children: ReactNode;
  isOpen: boolean;
  onToggle: (open: boolean) => void;
}) {
  const menuRef = useRef<HTMLDivElement>(null);
  const [mounted, setMounted] = useState(false);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setMounted(true);
      requestAnimationFrame(() => {
        requestAnimationFrame(() => setVisible(true));
      });
    } else {
      setVisible(false);
      const timer = setTimeout(() => setMounted(false), MENU_TRANSITION_MS);
      return () => clearTimeout(timer);
    }
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    function handleClickOutside(e: MouseEvent) {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        onToggle(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isOpen, onToggle]);

  return (
    <div ref={menuRef} className="relative">
      <button
        className="flex items-center justify-center p-1 text-black"
        onClick={() => onToggle(!isOpen)}
      >
        {isOpen ? (
          <X aria-label="Close navigation" size={26} />
        ) : (
          <List aria-label="Open navigation" size={26} />
        )}
      </button>

      {mounted && (
        <div
          className="absolute -right-3 top-full z-[7000] mt-1 w-[200px] origin-top-left rounded-md border bg-white py-1 shadow-md"
          style={{
            transition: `opacity ${MENU_TRANSITION_MS}ms ease, transform ${MENU_TRANSITION_MS}ms ease`,
            opacity: visible ? 1 : 0,
            transform: visible
              ? "rotate(0deg) translateY(0px)"
              : "rotate(5deg) translateY(25px)",
            pointerEvents: visible ? "auto" : "none",
          }}
        >
          {children}
        </div>
      )}
    </div>
  );
}

export function MobileMenuItem({
  children,
  onClick,
}: {
  children: ReactNode;
  onClick?: () => void;
}) {
  return (
    <div
      className="px-3 py-2 transition-colors hover:bg-gray-50 [&>button]:w-full"
      onClick={onClick}
    >
      {children}
    </div>
  );
}

export function MobileMenuDivider() {
  return <div className="my-1 border-t" />;
}

export default function Navbar({
  className,
  checkout,
  upload,
  compact,
}: {
  className?: string;
  checkout?: boolean;
  upload?: boolean;
  /**
   * Switch to the hamburger menu at a higher viewport width. Use when the
   * navbar doesn't span the full viewport (e.g. inside a grid column) so the
   * inline buttons would overflow before the default breakpoint.
   */
  compact?: boolean;
}) {
  const isProd = process.env.REACT_APP_PROD === "true";
  const { isLoggedIn } = useStore($account);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const cart = useStore($cart);
  const itemCount = cart?.lineItems?.length || 0;
  const loginModal = useLoginModal();
  const navigate = useNavigate();
  const location = useLocation();

  const handleToggleMenu = useCallback(
    (open: boolean) => setIsMenuOpen(open),
    []
  );

  useEffect(() => {
    CartService.getCart();
  }, []);

  useEffect(() => {
    if (!isLoggedIn) {
      setIsAdmin(false);
      if ($activeAdminCartId.get()) {
        CartService.clearAdminWorkspace();
      }
      return;
    }

    let isMounted = true;
    (async () => {
      try {
        const res = await UserApi.getUser();
        if (!isMounted) return;
        const admin = Boolean(res?.data?.user?.isAdmin);
        setIsAdmin(admin);
        if (admin) markStaffBrowser();
        if (!admin && $activeAdminCartId.get()) {
          await CartService.clearAdminWorkspace();
        }
      } catch {
        if (!isMounted) return;
        setIsAdmin(false);
      }
    })();

    return () => {
      isMounted = false;
    };
  }, [isLoggedIn]);

  const handleCartClick = useCallback(() => {
    setIsMenuOpen(false);
    CartService.toggleCart();
  }, []);

  const handleUploadClick = useCallback(() => {
    // Always go to the real upload page — never the popout uploader.
    setIsMenuOpen(false);
    navigate("/upload");
  }, [navigate]);

  const handleProfileClick = useCallback(() => {
    setIsMenuOpen(false);
    if (isLoggedIn) {
      navigate("/profile/details", {
        state: { from: location.pathname },
      });
    } else {
      loginModal.open();
    }
  }, [isLoggedIn, navigate, location.pathname, loginModal]);

  const ItemMyCart = () => (
    <NavButton
      label="My Cart"
      onClick={handleCartClick}
      padding={9}
      icon={
        <>
          <ShoppingCart weight="bold" size={24} />
          <div className="absolute right-0 top-0 grid size-5 place-items-center rounded-full border border-white bg-red-600 text-[12px] text-white">
            {itemCount}
          </div>
        </>
      }
    />
  );

  const DashboardButton = () => (
    <NavButton
      label="Dashboard"
      onClick={() => window.open("/dashboard", "_blank")}
      icon={<ChartBar weight="bold" size={24} />}
    />
  );

  const ItemUpload = () => (
    <NavButton label="Upload" onClick={handleUploadClick} variant="button" />
  );

  const ItemProfile = () => (
    <NavButton
      {...(isLoggedIn
        ? {
            label: "Profile",
            onClick: handleProfileClick,
            icon: <User weight="bold" size={24} />,
          }
        : {
            label: "Sign in",
            onClick: handleProfileClick,
            icon: <User weight="bold" size={24} />,
          })}
    />
  );

  const mobileMenuEntries: {
    key: string;
    content: ReactNode;
    onClick?: () => void;
  }[] = [
    ...(!checkout ? [{ key: "cart", content: <ItemMyCart /> }] : []),
    ...(!upload ? [{ key: "upload", content: <ItemUpload /> }] : []),
    ...(isAdmin
      ? [
          {
            key: "dashboard",
            content: <DashboardButton />,
            onClick: () => setIsMenuOpen(false),
          },
        ]
      : []),
    { key: "profile", content: <ItemProfile /> },
  ];

  return (
    <header
      className={cn(
        "flex h-[59px] shrink-0 items-center justify-between gap-12 border-b px-2 md:h-[67px] md:px-6",
        checkout && "mx-auto w-full max-w-[1560px] border-none",
        className
      )}
    >
      {isProd ? (
        <a href="https://www.tribecaprintworks.com/">
          <img src="/logo.png" className="max-w-[200px] md:max-w-[220px]" />
        </a>
      ) : (
        <Link to="/upload">
          <img src="/logo.png" className="max-w-[200px] md:max-w-[220px]" />
        </Link>
      )}

      {isLoggedIn && !checkout && <AdminOptionsMenu isAdmin={isAdmin} />}

      <div className="flex items-center gap-4">
        <div
          className={cn(
            "ml-auto hidden items-center justify-between gap-6",
            compact ? "xl:flex" : "lg:flex"
          )}
        >
          {!upload && <ItemUpload />}
          {!checkout && <ItemMyCart />}
          {isAdmin && <DashboardButton />}
          <ItemProfile />
        </div>

        <div
          className={cn(
            "flex items-center",
            compact ? "xl:hidden" : "lg:hidden"
          )}
        >
          <MobileMenu isOpen={isMenuOpen} onToggle={handleToggleMenu}>
            {mobileMenuEntries.map((entry, index) => (
              <Fragment key={entry.key}>
                {index > 0 && <MobileMenuDivider />}
                <MobileMenuItem onClick={entry.onClick}>
                  {entry.content}
                </MobileMenuItem>
              </Fragment>
            ))}
          </MobileMenu>
        </div>
      </div>
    </header>
  );
}
